import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import './AddLanguage.css';

const languages = [
    { code: 'italian', flag: '🇮🇹', name: 'Italian', native: 'Italiano' },
    { code: 'spanish', flag: '🇪🇸', name: 'Spanish', native: 'Español' },
    { code: 'french', flag: '🇫🇷', name: 'French', native: 'Français' },
    { code: 'english', flag: '🇬🇧', name: 'English', native: 'English' },
    { code: 'portuguese', flag: '🇧🇷', name: 'Portuguese', native: 'Português' },
];

const levelCodes = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export default function AddLanguage() {
    const navigate = useNavigate();
    useLanguage();
    const { userLanguages, addLanguage } = useUserLanguage();
    const [step, setStep] = useState('pick-language');
    const [selectedLang, setSelectedLang] = useState(null);
    const [selectedLevel, setSelectedLevel] = useState(null);

    const alreadyLearning = new Set(userLanguages.map(l => l.target_language));
    const availableLanguages = languages.filter(l => !alreadyLearning.has(l.code));

    const handlePickLanguage = (lang) => {
        setSelectedLang(lang);
        setStep('choose-level');
    };

    const handleBeginnerConfirm = async () => {
        await addLanguage(selectedLang.code, 'A1');
        navigate('/home');
    };

    const handleManualLevel = async () => {
        if (!selectedLevel) return;
        await addLanguage(selectedLang.code, selectedLevel);
        navigate('/home');
    };

    const handleTakeTest = () => {
        localStorage.setItem('vozoly_pending_new_language', selectedLang.code);
        localStorage.setItem('vozoly_placement_path', 'test');
        navigate('/onboarding/placement-test');
    };

    if (step === 'pick-language') {
        return (
            <div className="al-screen">
                <div className="al-top">
                    <button className="al-back" onClick={() => navigate('/home')}>✕</button>
                    <div className="al-title">Add a language</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="al-content">
                    <div className="al-heading">Which language do you want to learn?</div>
                    <div className="al-list">
                        {availableLanguages.map(lang => (
                            <div key={lang.code} className="al-lang-card" onClick={() => handlePickLanguage(lang)}>
                                <div className="al-lang-flag">{lang.flag}</div>
                                <div>
                                    <div className="al-lang-name">{lang.name}</div>
                                    <div className="al-lang-native">{lang.native}</div>
                                </div>
                                <span className="al-lang-arrow">›</span>
                            </div>
                        ))}
                    </div>
                    {availableLanguages.length === 0 && (
                        <div className="al-empty">You're already learning all available languages!</div>
                    )}
                </div>
            </div>
        );
    }

    if (step === 'choose-level') {
        return (
            <div className="al-screen">
                <div className="al-top">
                    <button className="al-back" onClick={() => setStep('pick-language')}>←</button>
                    <div className="al-title">{selectedLang.flag} {selectedLang.name}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="al-content">
                    <div className="al-heading">What's your level in {selectedLang.name}?</div>
                    <div className="al-sub">
                        Since you may already know some {selectedLang.name} even if you're at a different level in your other languages, let's find the right starting point.
                    </div>

                    <div className="al-option-card recommended" onClick={handleTakeTest}>
                        <div className="al-option-badge">Recommended</div>
                        <div className="al-option-title">Take the placement test</div>
                        <div className="al-option-desc">8-12 minutes for a precise result across grammar, listening, speaking and writing.</div>
                    </div>

                    <div className="al-option-card" onClick={handleBeginnerConfirm}>
                        <div className="al-option-title">I'm a complete beginner</div>
                        <div className="al-option-desc">Start from scratch at A1 — no test needed.</div>
                    </div>

                    <div className="al-option-card">
                        <div className="al-option-title">I already know my level</div>
                        <div className="al-level-grid">
                            {levelCodes.map(code => (
                                <button
                                    key={code}
                                    className={`al-level-btn ${selectedLevel === code ? 'selected' : ''}`}
                                    onClick={() => setSelectedLevel(code)}
                                >
                                    {code}
                                </button>
                            ))}
                        </div>
                        {selectedLevel && (
                            <button className="al-confirm-btn" onClick={handleManualLevel}>
                                Start at {selectedLevel} →
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return null;
}